import { useState, useEffect } from 'react';
import { useDebug } from '../context/DebugContext';
import { useBridge } from '../hooks/useHttpPolling';
import './Bridge.css';

interface BridgeTransaction {
  id: string;
  direction: 'deposit' | 'withdraw';
  status: 'pending' | 'confirming' | 'complete' | 'failed';
  amount: number;
  token: string;
  l1TxHash?: string;
  l2TxHash?: string;
  createdAt: string;
  completedAt?: string;
}

export default function Bridge() {
  const { addEvent } = useDebug();
  const { data: bridgeData, loading } = useBridge();
  const [bridgeAmount, setBridgeAmount] = useState('');
  const [direction, setDirection] = useState<'deposit' | 'withdraw'>('deposit');

  // Map API response to BridgeTransaction format
  const transactions: BridgeTransaction[] = (bridgeData?.transactions || []).map((tx: any) => ({
    id: tx.id,
    direction: tx.direction || 'deposit',
    status: tx.status || 'pending',
    amount: tx.amount || 0,
    token: tx.token || 'SOL',
    l1TxHash: tx.l1TxHash,
    l2TxHash: tx.l2TxHash,
    createdAt: tx.createdAt || new Date().toISOString(),
    completedAt: tx.completedAt,
  }));

  useEffect(() => {
    addEvent({
      source: 'console',
      category: 'navigation',
      message: 'Viewed Bridge page',
    });
  }, [addEvent]);

  const handleBridge = async () => {
    if (!bridgeAmount || parseFloat(bridgeAmount) <= 0) return;

    addEvent({
      source: 'console',
      category: 'api',
      message: `Initiating ${direction}: ${bridgeAmount} SOL`,
      data: { amount: bridgeAmount, direction },
    });

    // TODO: Call real bridge API when available
    console.log('Bridge transaction:', { direction, amount: bridgeAmount });
    setBridgeAmount('');
  };

  const getStatusColor = (status: BridgeTransaction['status']) => {
    switch (status) {
      case 'complete': return '#44ff88';
      case 'confirming': return '#ff8844';
      case 'pending': return '#4488ff';
      case 'failed': return '#ff4444';
      default: return '#888';
    }
  };

  return (
    <div className="bridge-page">
      <div className="page-header">
        <h2>Solana Bridge</h2>
      </div>

      <div className="bridge-layout">
        <div className="bridge-panel">
          <h3>Bridge Assets</h3>
          
          <div className="direction-toggle">
            <button
              className={`toggle-btn ${direction === 'deposit' ? 'active' : ''}`}
              onClick={() => setDirection('deposit')}
            >
              Deposit (L1 → L2)
            </button>
            <button
              className={`toggle-btn ${direction === 'withdraw' ? 'active' : ''}`}
              onClick={() => setDirection('withdraw')}
            >
              Withdraw (L2 → L1)
            </button>
          </div>

          <div className="bridge-form">
            <div className="chain-display">
              <div className="chain">
                <span className="chain-icon">◎</span>
                <span className="chain-name">
                  {direction === 'deposit' ? 'Solana' : 'eStream'}
                </span>
              </div>
              <span className="arrow">→</span>
              <div className="chain">
                <span className="chain-icon">⬡</span>
                <span className="chain-name">
                  {direction === 'deposit' ? 'eStream' : 'Solana'}
                </span>
              </div>
            </div>

            <div className="input-group">
              <input
                type="number"
                value={bridgeAmount}
                onChange={(e) => setBridgeAmount(e.target.value)}
                placeholder="0.00"
                className="amount-input"
              />
              <span className="token-label">SOL</span>
            </div>

            <div className="bridge-info">
              <div className="info-row">
                <span>Bridge Fee:</span>
                <span>0.1%</span>
              </div>
              <div className="info-row">
                <span>Estimated Time:</span>
                <span>~2 minutes</span>
              </div>
            </div>

            <button className="btn-bridge" onClick={handleBridge}>
              {direction === 'deposit' ? 'Deposit to eStream' : 'Withdraw to Solana'}
            </button>
          </div>
        </div>

        <div className="history-panel">
          <h3>Transaction History</h3>
          
          {loading ? (
            <div className="loading">Loading transactions...</div>
          ) : transactions.length === 0 ? (
            <div className="empty">No bridge transactions yet</div>
          ) : (
            <div className="tx-list">
              {transactions.map((tx) => (
                <div key={tx.id} className="tx-card">
                  <div className="tx-header">
                    <span className={`tx-direction ${tx.direction}`}>
                      {tx.direction === 'deposit' ? '↓ Deposit' : '↑ Withdraw'}
                    </span>
                    <span
                      className="tx-status"
                      style={{ color: getStatusColor(tx.status) }}
                    >
                      {tx.status}
                    </span>
                  </div>
                  <div className="tx-amount">
                    {tx.amount} {tx.token}
                  </div>
                  <div className="tx-meta">
                    <span>{new Date(tx.createdAt).toLocaleString()}</span>
                    {tx.l1TxHash && (
                      <a href="#" className="tx-link">L1: {tx.l1TxHash}</a>
                    )}
                    {tx.l2TxHash && (
                      <a href="#" className="tx-link">L2: {tx.l2TxHash}</a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
